/**
 * Asks before a dialog with unsaved edits closes (HS2-FSBQVX). Cancel, Escape, and
 * an outside press on a guarded dialog (`data-unsaved-guard`) are held while any of
 * its controls differs from the value it mounted with; the shared unsaved-changes
 * dialog then offers Keep editing (the dialog stays, as do Escape and an outside
 * press on the question), Discard (the dialog closes the way it was asked to), or
 * Save (the dialog's own Save runs: it closes on success and keeps its message on
 * failure).
 */
import { closeDialog } from '@kerfjs/ui/surface-scaffold';

import {
  changedDialogControls,
  GUARD_CANCEL,
  GUARD_SAVE,
  GUARDED_CONTROLS,
  GUARDED_DIALOG,
  recordInitialValues,
} from '../lib/unsaved-dialog.js';
import { dialogUnsavedPrompt } from './state.js';

interface HeldClose {
  dialog: HTMLElement;
  /** Close the dialog the way the viewer asked (Cancel, Escape, or an outside press). */
  discard: () => void;
}

let held: HeldClose | null = null;
/** Set while a held Cancel is replayed, so the guard lets that click through. */
let replaying = false;

/** Whether a guarded dialog has an edit to lose; a dialog that is already saving never asks. */
export function dialogHasUnsavedEdits(dialog: Element): boolean {
  if (dialog.querySelector(`${GUARD_SAVE}[loading]`) !== null) return false;
  return changedDialogControls(dialog).length > 0;
}

/** Whether any open guarded dialog under `root` has unsaved edits (for the browser leave prompt). */
export function anyDialogHasUnsavedEdits(root: ParentNode): boolean {
  return [...root.querySelectorAll(GUARDED_DIALOG)].some(dialogHasUnsavedEdits);
}

function hold(dialog: HTMLElement, discard: () => void): void {
  held = { dialog, discard };
  dialogUnsavedPrompt.value = { subject: dialog.dataset.unsavedGuard ?? 'this dialog' };
}

/** Whether the unsaved-changes question on screen is about a dialog (not a settings page). */
export function dialogPromptOpen(): boolean {
  return dialogUnsavedPrompt.value !== null;
}

function release(): HeldClose | null {
  const close = held;
  held = null;
  dialogUnsavedPrompt.value = null;
  return close;
}

/** Keep editing (also Escape and an outside press on the question): the dialog stays open. */
export function keepEditingDialog(): void {
  const close = release();
  const control = close === null ? undefined : changedDialogControls(close.dialog)[0];
  control?.focus?.();
}

/** Discard: close the dialog the way the viewer asked, dropping its edits. */
export function discardDialogEdits(): void {
  release()?.discard();
}

/** Save: press the dialog's own Save, which closes it on success and shows its message on failure. */
export function saveDialogEdits(): void {
  const close = release();
  close?.dialog.querySelector<HTMLElement>(GUARD_SAVE)?.click();
}

/** Record the mount value of every control in a guarded dialog under `node`. */
function recordUnder(node: Node): void {
  if (!(node instanceof Element)) return;
  const controls: Element[] = [];
  if (node.closest(GUARDED_DIALOG) !== null) {
    if (node.matches(GUARDED_CONTROLS)) controls.push(node);
    controls.push(...node.querySelectorAll(GUARDED_CONTROLS));
  } else {
    for (const dialog of node.querySelectorAll(GUARDED_DIALOG))
      controls.push(...dialog.querySelectorAll(GUARDED_CONTROLS));
  }
  recordInitialValues(controls);
}

/** Watch `root` for guarded dialogs and hold their Cancel, Escape, and outside-press closes. */
export function installDialogGuard(root: HTMLElement): void {
  recordUnder(root);
  new MutationObserver((records) => {
    for (const record of records) for (const node of record.addedNodes) recordUnder(node);
  }).observe(root, { childList: true, subtree: true });

  // Native DialogSurface close requests use a cancelable event before Kerf closes the dialog.
  root.addEventListener(
    'kui-request-close',
    (event) => {
      const dialog = event.target as HTMLElement;
      if (replaying || !dialog.matches('dialog[data-unsaved-guard]')) return;
      if (dialog.dataset.dialogBusy === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!dialogHasUnsavedEdits(dialog)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      hold(dialog, () => {
        closeDialog(dialog as HTMLDialogElement);
      });
    },
    { capture: true },
  );

  // Cancel: held before its delegated handler runs, and replayed on Discard.
  root.addEventListener(
    'click',
    (event) => {
      if (replaying) return;
      const button = (event.target as Element | null)?.closest<HTMLElement>(GUARD_CANCEL);
      const dialog = button?.closest<HTMLElement>(GUARDED_DIALOG);
      if (button === null || button === undefined || dialog === null || dialog === undefined) return;
      if (!dialogHasUnsavedEdits(dialog)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      hold(dialog, () => {
        replaying = true;
        try {
          button.click();
        } finally {
          replaying = false;
        }
      });
    },
    { capture: true },
  );
}
